import { ReactElement, useMemo } from 'react';
import { AlphaBaseDetail } from '../breakdownMath';
import { formatNumber } from '../breakdownFormat';
import DerivationChain from './DerivationChain';
import TraverseTimeSeries from '../TraverseTimeSeries';

interface Props {
  alphaBaseDetail: AlphaBaseDetail;
  // Pipeline AlphaBase_fG2 = AlphaBase_XG2 × TimeAbsDiff_tG3 — the per-gate integral
  // form computed by the gridded pipeline. Used as the canonical AlphaBase value.
  pipelineAlphaBaseF: number;
  // Pipeline Alpha_PG3 (per-traverse Alpha power from the pipeline). Used as the
  // displayed Alpha — no client-side fallback is computed.
  pipelineAlphaPower: number;
  duration: number;
  ts: number[];
  dynPresG21: number[];
  airYaw: number[];
  whlRevSpd: number[];
  cpCorr: number;
  wheelCircumference: number;
  gateStart: number;
  gateEnd: number;
}

function ChannelMissing({ name }: { name: string }): ReactElement {
  return (
    <span className="cr-text-red-700 cr-italic">
      ✗ <code>{name}</code> not in API payload
    </span>
  );
}

export default function AlphaSection({
  alphaBaseDetail,
  pipelineAlphaBaseF,
  pipelineAlphaPower,
  duration,
  ts,
  dynPresG21,
  airYaw,
  whlRevSpd,
  cpCorr,
  wheelCircumference,
  gateStart,
  gateEnd,
}: Props): ReactElement {
  // Canonical AlphaBase = pipeline AlphaBase_fG2.
  const alphaBase = pipelineAlphaBaseF;
  const alphaWork = Number.isFinite(pipelineAlphaPower) ? pipelineAlphaPower * duration : NaN;

  // Client-side independent integration of the three raw gridded inputs (sanity check
  // against the pipeline AlphaBase_fG2; should match within numerical precision).
  const clientAlphaBase = alphaBaseDetail.alphaBase;
  const clientAlphaBaseDelta = Number.isFinite(alphaBase) && Number.isFinite(clientAlphaBase)
    ? clientAlphaBase - alphaBase : NaN;
  const clientAlphaBaseDeltaPct = Number.isFinite(clientAlphaBaseDelta) && alphaBase !== 0
    ? (clientAlphaBaseDelta / alphaBase) * 100 : NaN;

  const cosYawSeries = useMemo(
    () => airYaw.map((y) => (Number.isFinite(y) ? Math.cos((y * Math.PI) / 180) : NaN)),
    [airYaw],
  );

  const alphaBaseAvailable = Number.isFinite(alphaBase);
  const alphaAvailable = Number.isFinite(pipelineAlphaPower);

  return (
    <div className="cr-py-2">
      {/* Three raw input plots: the building blocks of AlphaBase. */}
      <TraverseTimeSeries
        ts={ts}
        values={dynPresG21}
        gateStart={gateStart}
        gateEnd={gateEnd}
        yLabel="DynPres_pG21 (raw, no cp_corr)"
        unit="Pa"
        title="DynPres_pG21 — raw gridded dynamic pressure"
      />
      <div className="cr-h-3" />
      <TraverseTimeSeries
        ts={ts}
        values={cosYawSeries}
        gateStart={gateStart}
        gateEnd={gateEnd}
        yLabel="cos(AirYaw_AG31)"
        unit=""
        title="cos(AirYaw_AG31) — yaw projection onto road direction"
      />
      <div className="cr-h-3" />
      <TraverseTimeSeries
        ts={ts}
        values={whlRevSpd}
        gateStart={gateStart}
        gateEnd={gateEnd}
        yLabel="WhlRspd_nG3"
        unit="rev/s"
        title="WhlRspd_nG3 — wheel angular speed (revolutions per second)"
      />

      <div className="cr-mt-4">
        <DerivationChain
          steps={[
            {
              label: '∫ (DynPres_pG21 × cos(AirYaw_AG31) × WhlRspd_nG3) dt over gate',
              value: 'AlphaBase_fG2 (from pipeline)',
            },
            {
              label: '= AlphaBase',
              value: alphaBaseAvailable ? formatNumber(alphaBase, 2) : 'AlphaBase_fG2 missing',
              isResult: false,
            },
            { label: '× cp_corr', value: formatNumber(cpCorr, 4) },
            { label: '× wheel_circumference', value: `${formatNumber(wheelCircumference, 4)} m` },
            {
              label: '= Alpha (work)',
              value: alphaAvailable
                ? `${formatNumber(alphaWork, 0)} J`
                : 'Alpha_PG3 missing',
              isResult: true,
            },
            { label: '÷ Time', value: `${formatNumber(duration, 2)} s` },
            {
              label: '= Alpha power',
              value: alphaAvailable
                ? `${formatNumber(pipelineAlphaPower, 1)} W`
                : 'Alpha_PG3 missing',
              isResult: true,
            },
          ]}
        />
      </div>

      <div className="cr-mt-3 cr-pt-3 cr-border-t cr-border-gray-200 cr-text-sm">
        <div className="cr-text-gray-700 cr-font-medium cr-mb-1">
          AlphaBase verification (client integration vs pipeline)
        </div>
        <div className="cr-grid cr-grid-cols-2 cr-gap-3 cr-font-mono cr-text-gray-700">
          <div>
            <div className="cr-text-xs cr-text-gray-500">Client ∫ over gridded inputs</div>
            <div>
              {Number.isFinite(clientAlphaBase) ? formatNumber(clientAlphaBase, 2) : 'n/a'}
            </div>
          </div>
          <div>
            <div className="cr-text-xs cr-text-gray-500">Pipeline AlphaBase_fG2</div>
            <div>
              {alphaBaseAvailable ? formatNumber(alphaBase, 2) : <ChannelMissing name="AlphaBase_fG2" />}
              {Number.isFinite(clientAlphaBaseDeltaPct) && (
                <span className={`cr-ml-2 cr-text-xs ${
                  Math.abs(clientAlphaBaseDeltaPct) > 0.5 ? 'cr-text-amber-700' : 'cr-text-gray-500'
                }`}>
                  (Δ {clientAlphaBaseDeltaPct >= 0 ? '+' : ''}
                  {formatNumber(clientAlphaBaseDeltaPct, 2)}%)
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <p className="cr-text-xs cr-text-gray-500 cr-mt-3 cr-italic">
        Pipeline reference: <code>cda_post_processing.py</code>:208-210 builds{' '}
        <code>AlphaBase = Σ DynPres_pG21 × cos(AirYaw_AG31) × Δrev</code> over the grid,
        stored as <code>AlphaBase_XG2</code>.{' '}
        <code>cdacalcterms.py</code> produces{' '}
        <code>AlphaBase_fG2 = AlphaBase_XG2 × TimeAbsDiff_tG3</code> (per-gate integral form
        used here) and <code>Alpha_PG3 = AlphaBase × cp_corr × wheel_circumference / ΔT</code>.
        Live <code>cp_corr</code> = {formatNumber(cpCorr, 4)},{' '}
        <code>wheel_circumference</code> = {formatNumber(wheelCircumference, 4)} m.
      </p>
    </div>
  );
}
